import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import '../../styles/websitepagescss/Home.css';

// Import your 11 images
import constructionHero1 from '../../assets/home/BANNER1.jpg';
import constructionHero2 from '../../assets/home/BANNER2.jpg';
import constructionHero3 from '../../assets/home/BANNER3.jpg';
import constructionHero4 from '../../assets/home/BANNER4.jpg';
import constructionHero5 from '../../assets/home/BANNER5.jpg';
import constructionHero6 from '../../assets/home/BANNER6.jpg';
import constructionHero7 from '../../assets/home/BANNER7.jpg';
import constructionHero8 from '../../assets/home/BANNER8.jpg';
import constructionHero9 from '../../assets/home/BANNER9.jpg';
import constructionHero10 from '../../assets/home/BANNER10.jpg';
import constructionHero11 from '../../assets/home/BANNER11.jpg';

const Home = () => {
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const navigate = useNavigate();

  const sliderImages = [
    {
      id: 1,
      src: constructionHero1,
      alt: "Modern Construction Project",
      title: "Building Your Vision",
      description: "Transforming architectural dreams into structural realities with precision and expertise"
    },
    {
      id: 2,
      src: constructionHero2,
      alt: "Construction Excellence",
      title: "Quality Craftsmanship",
      description: "Delivering exceptional construction quality with attention to every detail"
    },
    {
      id: 3,
      src: constructionHero3,
      alt: "Professional Construction Team",
      title: "Expert Engineering",
      description: "Combining innovative design with robust engineering solutions"
    },
    {
      id: 4,
      src: constructionHero4,
      alt: "Modern Architecture",
      title: "Innovative Designs",
      description: "Creating stunning architectural marvels that stand the test of time"
    },
    {
      id: 5,
      src: constructionHero5,
      alt: "Construction Site",
      title: "Project Management",
      description: "Efficient project coordination ensuring seamless execution from start to finish"
    },
    {
      id: 6,
      src: constructionHero6,
      alt: "Building Construction",
      title: "Sustainable Building",
      description: "Eco-friendly construction practices for a greener tomorrow"
    },
    {
      id: 7,
      src: constructionHero7,
      alt: "Interior Construction",
      title: "Interior Excellence",
      description: "Transforming spaces with exquisite interior design and finishing"
    },
    {
      id: 8,
      src: constructionHero8,
      alt: "Commercial Construction",
      title: "Commercial Projects",
      description: "Building commercial spaces that drive business success and growth"
    },
    {
      id: 9,
      src: constructionHero9,
      alt: "Residential Construction",
      title: "Residential Masterpieces",
      description: "Creating dream homes with unparalleled craftsmanship and care"
    },
    {
      id: 10,
      src: constructionHero10,
      alt: "Infrastructure Development",
      title: "Infrastructure Solutions",
      description: "Developing robust infrastructure for communities and cities"
    },
    {
      id: 11,
      src: constructionHero11,
      alt: "Construction Technology",
      title: "Advanced Technology",
      description: "Leveraging cutting-edge technology for superior construction outcomes"
    }
  ];

  // Auto-slide functionality with adjusted timing for 11 slides
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % sliderImages.length);
    }, 5000); // You might want to adjust this timing for 11 slides
    return () => clearInterval(interval);
  }, [sliderImages.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % sliderImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + sliderImages.length) % sliderImages.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const features = [
    {
      id: 1,
      icon: "🏗️",
      title: "Quality Construction",
      shortDesc: "Premium materials and advanced construction techniques for lasting results.",
      fullDesc: "Our commitment to quality construction is unwavering. We utilize premium materials, advanced construction techniques, and rigorous quality control processes.",
      benefits: [
        "Premium materials from trusted suppliers",
        "Advanced construction methodologies",
        "Rigorous quality control at every stage",
        "Sustainable and eco-friendly practices"
      ]
    },
    {
      id: 2,
      icon: "⏱️",
      title: "On-Time Delivery",
      shortDesc: "Precise project management ensuring timely completion without compromises.",
      fullDesc: "Time is money, and we respect both. Our project management expertise ensures that every project is delivered on schedule.",
      benefits: [
        "Advanced project planning and scheduling",
        "Efficient resource management",
        "Proactive risk mitigation",
        "Regular progress monitoring"
      ]
    },
    {
      id: 3,
      icon: "💼",
      title: "Professional Team",
      shortDesc: "Certified experts with decades of experience in construction excellence.",
      fullDesc: "Our team comprises industry veterans, certified professionals, and skilled craftsmen who bring decades of combined experience.",
      benefits: [
        "Certified and licensed professionals",
        "Decades of combined experience",
        "Continuous training and development",
        "Specialized expertise across sectors"
      ]
    },
    {
      id: 4,
      icon: "💰",
      title: "Competitive Pricing",
      shortDesc: "Transparent pricing with exceptional value and no hidden costs.",
      fullDesc: "We believe in transparent, competitive pricing without hidden costs. Our efficient operations ensure exceptional value.",
      benefits: [
        "Transparent cost breakdowns",
        "No hidden charges",
        "Value engineering solutions",
        "Cost optimization without quality compromise"
      ]
    },
    {
      id: 5,
      icon: "🛡️",
      title: "Safety First",
      shortDesc: "Rigorous safety protocols ensuring zero accidents on all sites.",
      fullDesc: "Safety is our top priority. We implement rigorous safety protocols and state-of-the-art safety equipment.",
      benefits: [
        "OSHA-compliant safety protocols",
        "Regular safety training and drills",
        "Advanced safety equipment",
        "Daily safety inspections"
      ]
    },
    {
      id: 6,
      icon: "🌱",
      title: "Sustainable Solutions",
      shortDesc: "Eco-friendly practices and green building technologies integrated.",
      fullDesc: "We integrate sustainable practices into every project, from material selection to construction methods.",
      benefits: [
        "LEED certification support",
        "Energy-efficient designs",
        "Sustainable material sourcing",
        "Waste reduction strategies"
      ]
    }
  ];

  const stats = [
    { 
      number: "263", 
      text: "Projects Delivered", 
      icon: "🏆",
      suffix: "+"
    },
    { 
      number: "17", 
      text: "Years of Excellence", 
      icon: "⭐",
      suffix: ""
    },
    { 
      number: "43", 
      text: "Expert Professionals", 
      icon: "👷",
      suffix: "+"
    },
    { 
      number: "5", 
      text: "Industry Awards", 
      icon: "🎯",
      suffix: "+"
    }
  ];

  const handleGetStarted = () => {
    navigate('/contact');
  };

  return (
    <div className="home">
      {/* Hero Slider Section */}
      <section className="hero-slider">
        <div className="slider-container">
          <div className="slider-wrapper">
            {sliderImages.map((slide, index) => (
              <div
                key={slide.id}
                className={`slide ${index === currentSlide ? 'active' : ''}`}
                style={{ backgroundImage: `url(${slide.src})` }}
              >
                <div className="slide-overlay"></div>
                
                <div className="slide-content">
                  <div className="content-wrapper">
                    <div className="text-content">
                      <h1 className="main-title">
                        Building <span className="highlight">Excellence</span><br />in Every Project
                      </h1>
                      
                      <p className="slide-description">
                        {slide.description}
                      </p>
                      
                      <div className="button-group">
                        <button 
                          className="cta-button primary"
                          onClick={handleGetStarted}
                        >
                          Start Your Project
                          <span className="button-arrow">→</span>
                        </button>
                        <button 
                          className="cta-button secondary"
                          onClick={() => navigate('/projects')}
                        >
                          View Our Work
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Simple Slider Controls */}
            <div className="slider-controls">
              <button 
                className="control-btn prev" 
                onClick={prevSlide}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              
              <div className="slide-indicators">
                {sliderImages.map((_, index) => (
                  <button
                    key={index}
                    className={`slide-indicator ${index === currentSlide ? 'active' : ''}`}
                    onClick={() => goToSlide(index)}
                  >
                    <span className="indicator-dot"></span>
                  </button>
                ))}
              </div>
              
              <button 
                className="control-btn next" 
                onClick={nextSlide}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Glassmorphism Stats Section */}
        <div className="stats-section">
          <div className="container">
            <div className="stats-grid">
              {stats.map((stat, index) => (
                <motion.div 
                  key={stat.text}
                  className="stat-card"
                  initial={{ opacity: 0, y: 30, scale: 0.9 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ 
                    duration: 0.6, 
                    delay: index * 0.1,
                    type: "spring",
                    stiffness: 100
                  }}
                  whileHover={{ 
                    scale: 1.05,
                    y: -8,
                    transition: { duration: 0.3 }
                  }}
                  whileTap={{ scale: 0.95 }}
                >
                  <div className="stat-glass-effect"></div>
                  <div className="stat-border-glow"></div>
                  
                  <div className="stat-icon-wrapper">
                    <div className="stat-icon">{stat.icon}</div>
                  </div>
                  
                  <div className="stat-content">
                    <div className="stat-number">
                      {stat.number}<span className="stat-suffix">{stat.suffix}</span>
                    </div>
                    <div className="stat-text">{stat.text}</div>
                  </div>
                  
                  <div className="stat-shine"></div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="why-choose-us">
        <div className="container">
          <div className="section-header">
            <h2>Why Choose US</h2>
            <p>Excellence in construction through innovation, integrity, and unparalleled craftsmanship</p>
          </div>
          
          <div className="features-grid">
            {features.map((feature, index) => (
              <div
                key={feature.id}
                className="feature-card"
                onClick={() => setSelectedFeature(feature)}
              >
                <div className="feature-icon-wrapper">
                  <div className="feature-icon">{feature.icon}</div>
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.shortDesc}</p>
                <div className="feature-cta">
                  <span>Learn More</span>
                  <span className="arrow">→</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section with Straight Edges */}
      <section className="home-section">
        <div className="container">
          <div className="cta-content">
            <div className="cta-text">
              <h2>Ready to Start Your Construction Project?</h2>
              <p>Let's discuss your vision and turn it into reality with our expert construction services.</p>
            </div>
            <button 
              className="cta-button primary large"
              onClick={handleGetStarted}
            >
              Get Free Consultation
              <span className="button-arrow">→</span>
            </button>
          </div>
        </div>
      </section>

      {/* Feature Modal */}
      {selectedFeature && (
        <div 
          className="feature-modal-overlay"
          onClick={() => setSelectedFeature(null)}
        >
          <div 
            className="feature-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              className="close-button"
              onClick={() => setSelectedFeature(null)}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
            
            <div className="modal-header">
              <div className="modal-icon">{selectedFeature.icon}</div>
              <h2>{selectedFeature.title}</h2>
            </div>
            
            <p className="modal-description">{selectedFeature.fullDesc}</p>
            
            <div className="feature-benefits">
              <h4>Key Benefits</h4>
              <ul>
                {selectedFeature.benefits.map((benefit, index) => (
                  <li key={index}>
                    <span className="benefit-icon">✓</span>
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>
            
            <button className="modal-cta-button" onClick={handleGetStarted}>
              Start Your Project
              <span className="button-arrow">→</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;